// =============================================================================
// MeuDriver — Admin: "Precisa de você hoje"
// Lê admin_attention_summary (a mesma RPC do resumo diário no WhatsApp/n8n).
// =============================================================================

import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CalendarClock, CheckCircle2, Gavel, ShieldCheck, UserCheck, Wallet, LifeBuoy, BellOff } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { formatCurrency } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

interface Summary {
  instructors_pending: number
  disputes_open: number
  lgpd_open: number
  lgpd_due_3d: number
  tickets_open: number
  payouts_due: number
  payouts_due_amount: number
  lessons_today: number
  lessons_stuck: number
  outbox_failed_24h: number
}

export function AdminAttentionPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-attention'],
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_attention_summary')
      if (error) throw error
      return data as Summary
    },
  })

  const items = data
    ? [
        { n: data.lessons_stuck, label: 'aula(s) passaram do horário sem registro', to: '/admin/disputas', icon: CalendarClock, urgent: true },
        { n: data.lgpd_open, label: `pedido(s) LGPD${data.lgpd_due_3d ? ` — ${data.lgpd_due_3d} vencendo` : ''}`, to: '/admin/lgpd', icon: ShieldCheck, urgent: data.lgpd_due_3d > 0 },
        { n: data.disputes_open, label: 'disputa(s) aberta(s)', to: '/admin/disputas', icon: Gavel, urgent: true },
        { n: data.instructors_pending, label: 'instrutor(es) aguardando aprovação', to: '/admin/instrutores/aprovacao', icon: UserCheck, urgent: false },
        { n: data.payouts_due, label: `repasse(s) a pagar — ${formatCurrency(Number(data.payouts_due_amount))}`, to: '/admin/financeiro?aba=repasses', icon: Wallet, urgent: false },
        { n: data.tickets_open, label: 'ticket(s) de suporte aberto(s)', to: '/admin/suporte', icon: LifeBuoy, urgent: false },
        { n: data.outbox_failed_24h, label: 'aviso(s) que não saíram nas últimas 24 h', to: '/admin', icon: BellOff, urgent: false },
      ].filter((i) => i.n > 0)
    : []

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> Precisa de você hoje
          </span>
          {data && <span className="text-sm font-normal text-muted-foreground">{data.lessons_today} aula(s) hoje</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : items.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-green-500" /> Nada pendente.
          </p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {items.map((i) => (
              <Link key={i.label} to={i.to}
                className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors hover:bg-muted/40 ${i.urgent ? 'border-red-200 bg-red-50/60' : ''}`}>
                <i.icon className={`h-4 w-4 shrink-0 ${i.urgent ? 'text-red-600' : 'text-gray-500'}`} />
                <span><strong>{i.n}</strong> {i.label}</span>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
